// Editorial empty state. Replaces "grey circle + 'No items yet'" generics.
// One display-serif sentence, one mono tip, one primary CTA with a kbd hint.
// Used across DeckList, PlaytestHistory, CardSearch no-results, etc.

import type React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import Kbd from "./Kbd";

interface Props {
  title: string;
  body?: string;
  /** Primary action. If provided, rendered as a button/link child. */
  action?: React.ReactNode;
  /** Mono tip shown under the action (e.g. "tip: ⌘P from any deck"). */
  tip?: React.ReactNode;
  /** Keyboard shortcut for the action, rendered as a chip next to it. */
  kbd?: string;
  /** Optional icon. Used sparingly — most empties work better without. */
  icon?: LucideIcon;
  className?: string;
}

export default function EmptyState({ title, body, action, tip, kbd, icon: Icon, className }: Props) {
  return (
    <div
      className={cn(
        "mx-auto flex max-w-lg flex-col items-start gap-4 py-14 md:py-20",
        className,
      )}
    >
      {Icon && (
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bg-surface/60">
          <Icon size={18} strokeWidth={1.5} className="text-fg-muted" />
        </div>
      )}
      <h3 className="font-display text-2xl md:text-3xl font-medium tracking-[-0.01em] leading-[1.1]">
        {title}
      </h3>
      {body && (
        <p className="max-w-md text-sm text-fg-muted leading-relaxed">{body}</p>
      )}
      {action && (
        <div className="mt-2 flex items-center gap-3">
          {action}
          {kbd && <Kbd combo={kbd} />}
        </div>
      )}
      {tip && (
        <div className="mt-2 font-mono text-[11px] text-fg-subtle">
          {tip}
        </div>
      )}
    </div>
  );
}
